import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreCatalogItem } from '../../models';
import { formatCurrency } from '../../utils/currency';
import { safeImageUrl } from '../../utils/url';
import { getPixPrice, getInstallmentInfo } from '../../utils/pricing';
import { usePaymentSettings } from '../../hooks/usePaymentSettings';
import { useQuickAdd } from '../../hooks/useQuickAdd';
import { QuickAddModal } from './QuickAddModal';
import { productPlaceholderIcon } from '../../utils/productPlaceholder';
import { getGenderBadge } from '../../utils/gender';

const SWIPE_THRESHOLD_PX = 40;
const SWIPE_AXIS_RATIO = 1.5;
const FINE_POINTER_ONLY = '[@media(hover:hover)]:flex';

interface ProductCardProps {
  product: StoreCatalogItem;
  colorId?: number;
  variationValue?: string;
  priority?: boolean;
  swipe?: boolean;
}

export function ProductCard({ product, colorId, variationValue, priority = false, swipe = true }: ProductCardProps) {
  const displayPrice = product.promotionalPrice ?? product.price;
  const hasPromotion = !!product.promotionalPrice;
  const hasPrice = product.hasPrice && displayPrice !== null;
  const discountPercentage =
    hasPromotion && hasPrice && product.price
      ? Math.round((1 - (product.promotionalPrice as number) / product.price) * 100)
      : null;

  const images = (product.imageUrls?.length ? product.imageUrls : product.mainImageUrl ? [product.mainImageUrl] : []).filter(Boolean);
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasMultipleImages = images.length > 1;
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const { settings: paymentSettings } = usePaymentSettings();
  const pixPrice =
    hasPrice && paymentSettings && !hasPromotion
      ? getPixPrice(displayPrice as number, paymentSettings.pixDiscountPercentage)
      : null;
  const installmentInfo =
    hasPrice && paymentSettings && !hasPromotion ? getInstallmentInfo(displayPrice as number, paymentSettings) : null;

  const { isFetching, modalProduct, openQuickAdd, closeQuickAdd } = useQuickAdd(product.id);
  const genderBadge = getGenderBadge(product.gender);

  const linkParams = new URLSearchParams();
  if (colorId !== undefined) linkParams.set('color', String(colorId));
  if (variationValue !== undefined) linkParams.set('var', variationValue);
  const linkTo = `/produtos/${product.id}${linkParams.size > 0 ? `?${linkParams.toString()}` : ''}`;

  const showPrev = () => setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const showNext = () => setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!swipe || !hasMultipleImages) return;
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - start.x;
    const deltaY = touch.clientY - start.y;
    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_AXIS_RATIO) return;
    if (deltaX < 0) showNext();
    else showPrev();
  };

  const arrowClasses = `hidden ${FINE_POINTER_ONLY} absolute top-1/2 -translate-y-1/2 pointer-events-auto w-8 h-8 rounded-full bg-white/90 hover:bg-white text-text-dark items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity duration-200 shadow-sm`;

  return (
    <>
      <div className="group relative h-full bg-white rounded-xl overflow-hidden border border-champagne shadow-sm hover:border-champagne-dark transition-all duration-200 flex flex-col">
        <Link to={linkTo} className="flex flex-col flex-1 min-w-0">
          <div
            className="relative aspect-square bg-champagne-light overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            {images.length > 0 ? (
              <img
                src={safeImageUrl(images[currentIndex])}
                alt={product.name}
                draggable={false}
                loading={priority ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={productPlaceholderIcon(product.id)}
                  alt=""
                  loading={priority ? 'eager' : 'lazy'}
                  decoding="async"
                  className="w-16 h-16 opacity-70"
                />
              </div>
            )}

            {hasPromotion && product.hasStock && (
              <span className="absolute top-2 left-2 bg-text-dark text-white text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
                Oferta
              </span>
            )}

            {hasMultipleImages && (
              <div aria-hidden="true" className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                {images.map((_, idx) => (
                  <span
                    key={idx}
                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                      idx === currentIndex ? 'bg-chocolate-dark' : 'bg-white/70'
                    }`}
                  />
                ))}
              </div>
            )}

            {!product.hasStock && (
              <div className="absolute inset-0 bg-text-dark/50 backdrop-blur-[1px] flex items-center justify-center">
                <span className="bg-white text-text-dark text-xs font-semibold px-2.5 py-1 rounded-md">
                  Sem estoque
                </span>
              </div>
            )}
          </div>

          <div className="p-3 sm:p-3.5 flex flex-col gap-1 flex-1">
            <p className="hidden sm:block text-xs font-semibold uppercase tracking-wide text-chocolate-dark/80">
              {product.categoryName}
              {genderBadge && (
                <>
                  {' · '}
                  <span className={genderBadge.className}>{genderBadge.label}</span>
                </>
              )}
            </p>
            <h3 className="font-semibold text-text-dark text-base leading-snug line-clamp-2">{product.name}</h3>
            <div className="mt-auto pt-2 flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
              {hasPrice ? (
                <>
                  {hasPromotion && (
                    <span className="whitespace-nowrap text-text-light text-xs sm:text-sm line-through [font-variant-numeric:tabular-nums]">
                      R$ {formatCurrency(product.price as number)}
                    </span>
                  )}
                  <span className={`whitespace-nowrap text-base sm:text-lg font-bold [font-variant-numeric:tabular-nums] ${hasPromotion ? 'text-chocolate-dark' : 'text-text-dark'}`}>
                    R$ {formatCurrency(displayPrice as number)}
                  </span>
                  {discountPercentage !== null && discountPercentage > 0 && (
                    <span className="hidden sm:inline-block whitespace-nowrap bg-olivine/15 text-olivine-dark text-xs font-bold px-1.5 py-0.5 rounded">
                      -{discountPercentage}%
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm font-medium text-text-light">Consulte-nos</span>
              )}
            </div>
            {hasPrice && paymentSettings && paymentSettings.pixDiscountPercentage > 0 && (
              <p className={`text-sm text-olivine-dark font-semibold [font-variant-numeric:tabular-nums] ${pixPrice !== null ? '' : 'invisible'}`}>
                {pixPrice !== null ? `R$ ${formatCurrency(pixPrice)} no Pix` : ' '}
              </p>
            )}
            {hasPrice && paymentSettings && paymentSettings.minInstallmentValue > 0 && (
              <p className={`hidden sm:block text-sm text-text-light [font-variant-numeric:tabular-nums] ${installmentInfo ? '' : 'invisible'}`}>
                {installmentInfo ? `ou ${installmentInfo.count}x de R$ ${formatCurrency(installmentInfo.amount)}` : ' '}
              </p>
            )}
          </div>
        </Link>

        <div className="absolute inset-x-0 top-0 aspect-square pointer-events-none">
          {hasMultipleImages && (
            <>
              <button
                type="button"
                onClick={showPrev}
                aria-label="Imagem anterior"
                className={`${arrowClasses} left-1.5`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={showNext}
                aria-label="Próxima imagem"
                className={`${arrowClasses} right-1.5`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {product.hasStock && (
            <button
              type="button"
              onClick={openQuickAdd}
              disabled={isFetching}
              aria-label="Adicionar à sacola"
              className="absolute bottom-0.5 right-0.5 pointer-events-auto w-11 h-11 flex items-center justify-center group/add disabled:cursor-wait"
            >
              <span className="w-9 h-9 rounded-full bg-white/90 group-hover/add:bg-white text-chocolate-dark flex items-center justify-center shadow-sm">
                {isFetching ? (
                  <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14m-7-7h14" />
                  </svg>
                )}
              </span>
            </button>
          )}
        </div>
      </div>
      <QuickAddModal product={modalProduct} onClose={closeQuickAdd} />
    </>
  );
}
